import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, CircularProgress, Alert,
  IconButton, InputAdornment, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
  ArrowBack as BackIcon, ArrowForward as ArrowIcon, Visibility, VisibilityOff,
  CheckCircleRounded as CheckIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { isVoiceReference } from '../../utils/voiceDevice';
import { useTranslation } from 'react-i18next';
import userApi from '../../api/userApi';

// Real flag images (flag emojis don't render on Windows/Chrome)
const LANGS = [
  { code: 'en', label: 'English', flag: 'gb' },
  { code: 'de', label: 'Deutsch', flag: 'de' },
  { code: 'it', label: 'Italiano', flag: 'it' },
  { code: 'fr', label: 'Français', flag: 'fr' },
  { code: 'es', label: 'Español', flag: 'es' }
];
const flagSrc = (c) => `https://flagcdn.com/${c}.svg`;

const T = {
  de: {
    tagline: 'ERP & Service Portal',
    title: 'Anmelden', subtitle: 'Bei Ihrem Konto anmelden', resetSubtitle: 'Setzen Sie Ihr Passwort zurück.',
    methodCode: 'Mit Code', methodPassword: 'Mit Passwort',
    emailLabel: 'E-Mail-Adresse', passwordLabel: 'Passwort', login: 'Anmelden',
    identifierPlaceholder: 'name@beispiel.ch',
    sendCode: 'Code senden', codeSent: 'Wir haben Ihnen einen 6-stelligen Code gesendet an',
    codeLabel: 'Bestätigungscode', verify: 'Verifizieren & Anmelden', changeEmail: 'E-Mail ändern',
    forgot: 'Passwort vergessen?', resetTitle: 'Passwort zurücksetzen',
    resetHelp: 'Wir senden Anweisungen an diese E-Mail.', sendReset: 'Anweisungen senden',
    backToLogin: 'Zurück zur Anmeldung', resetSent: 'Falls ein Konto mit dieser E-Mail-Adresse besteht, wurden die Anweisungen versendet.', language: 'Sprache',
    errCreds: 'Bitte E-Mail und Passwort eingeben.', errNoId: 'Bitte E-Mail-Adresse eingeben.',
    errCode: 'Bitte den 6-stelligen Code eingeben.', networkError: 'Keine Verbindung zum Server. Bitte prüfen Sie Ihre Internetverbindung.',
    invalidCredentials: 'E-Mail-Adresse oder Passwort ist falsch.', unauthorized: 'Ihr Konto hat keinen Zugriff auf das System.',
    noAccount: 'Für diese E-Mail-Adresse wurde kein Konto gefunden. Melden Sie sich mit Passwort an oder wenden Sie sich an die Administration.',
    invalidCode: 'Der Bestätigungscode ist ungültig oder abgelaufen.', loginFailed: 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    codeRequestFailed: 'Der Bestätigungscode konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
    validEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.', showPassword: 'Passwort anzeigen', hidePassword: 'Passwort verbergen'
  },
  en: {
    tagline: 'ERP & Service Portal',
    title: 'Sign in', subtitle: 'Sign in to your account', resetSubtitle: 'Reset your password.',
    methodCode: 'With code', methodPassword: 'With password',
    emailLabel: 'Email address', passwordLabel: 'Password', login: 'Login',
    identifierPlaceholder: 'name@example.com',
    sendCode: 'Send code', codeSent: 'We sent a 6-digit code to',
    codeLabel: 'Verification code', verify: 'Verify & sign in', changeEmail: 'Change email',
    forgot: 'Forgot password?', resetTitle: 'Reset password',
    resetHelp: "We'll send reset instructions to this email.", sendReset: 'Send reset instructions',
    backToLogin: 'Back to login', resetSent: 'If an account with that email exists, instructions have been sent.', language: 'Language',
    errCreds: 'Please enter both email and password.', errNoId: 'Please enter your email address.',
    errCode: 'Please enter the 6-digit code.', networkError: 'Unable to connect to the server. Please check your network connection.',
    invalidCredentials: 'The email address or password is incorrect.', unauthorized: 'Your account is not authorized to access the system.',
    noAccount: 'No account was found for this email. Sign in with a password or contact your administrator.',
    invalidCode: 'The verification code is invalid or has expired.', loginFailed: 'Sign-in failed. Please try again.',
    codeRequestFailed: 'The verification code could not be sent. Please try again.',
    validEmail: 'Please enter a valid email address.', showPassword: 'Show password', hidePassword: 'Hide password'
  },
  it: {
    tagline: 'Portale ERP e assistenza',
    title: 'Accedi', subtitle: 'Accedi al tuo account', resetSubtitle: 'Reimposta la password.',
    methodCode: 'Con codice', methodPassword: 'Con password',
    emailLabel: 'Indirizzo e-mail', passwordLabel: 'Password', login: 'Accedi',
    identifierPlaceholder: 'nome@esempio.ch',
    sendCode: 'Invia codice', codeSent: 'Abbiamo inviato un codice di 6 cifre a',
    codeLabel: 'Codice di verifica', verify: 'Verifica e accedi', changeEmail: 'Modifica e-mail',
    forgot: 'Password dimenticata?', resetTitle: 'Reimposta la password',
    resetHelp: 'Invieremo le istruzioni a questo indirizzo e-mail.', sendReset: 'Invia istruzioni',
    backToLogin: 'Torna all’accesso', resetSent: 'Se esiste un account con questo indirizzo e-mail, le istruzioni sono state inviate.', language: 'Lingua',
    errCreds: 'Inserisci l’indirizzo e-mail e la password.', errNoId: 'Inserisci il tuo indirizzo e-mail.',
    errCode: 'Inserisci il codice di 6 cifre.', networkError: 'Impossibile connettersi al server. Controlla la connessione Internet.',
    invalidCredentials: 'L’indirizzo e-mail o la password non sono corretti.', unauthorized: 'Il tuo account non è autorizzato ad accedere al sistema.',
    noAccount: 'Non è stato trovato un account con questo indirizzo e-mail. Accedi con la password o contatta l’amministratore.',
    invalidCode: 'Il codice di verifica non è valido o è scaduto.', loginFailed: 'Accesso non riuscito. Riprova.',
    codeRequestFailed: 'Impossibile inviare il codice di verifica. Riprova.',
    validEmail: 'Inserisci un indirizzo e-mail valido.', showPassword: 'Mostra password', hidePassword: 'Nascondi password'
  },
  fr: {
    tagline: 'Portail ERP & Service',
    title: 'Connexion', subtitle: 'Connectez-vous à votre compte', resetSubtitle: 'Réinitialisez votre mot de passe.',
    methodCode: 'Avec code', methodPassword: 'Avec mot de passe',
    emailLabel: 'Adresse e-mail', passwordLabel: 'Mot de passe', login: 'Connexion',
    identifierPlaceholder: 'nom@exemple.ch',
    sendCode: 'Envoyer le code', codeSent: 'Nous avons envoyé un code à 6 chiffres à',
    codeLabel: 'Code de vérification', verify: 'Vérifier et se connecter', changeEmail: "Changer l'e-mail",
    forgot: 'Mot de passe oublié ?', resetTitle: 'Réinitialiser le mot de passe',
    resetHelp: 'Nous enverrons les instructions à cet e-mail.', sendReset: 'Envoyer les instructions',
    backToLogin: 'Retour à la connexion', resetSent: 'Si un compte correspond à cette adresse e-mail, les instructions ont été envoyées.', language: 'Langue',
    errCreds: 'Veuillez saisir votre e-mail et mot de passe.', errNoId: 'Veuillez saisir votre adresse e-mail.',
    errCode: 'Veuillez saisir le code à 6 chiffres.', networkError: 'Impossible de joindre le serveur. Vérifiez votre connexion Internet.',
    invalidCredentials: 'L’adresse e-mail ou le mot de passe est incorrect.', unauthorized: 'Votre compte n’est pas autorisé à accéder au système.',
    noAccount: 'Aucun compte ne correspond à cette adresse e-mail. Connectez-vous avec un mot de passe ou contactez votre administrateur.',
    invalidCode: 'Le code de vérification est invalide ou a expiré.', loginFailed: 'La connexion a échoué. Veuillez réessayer.',
    codeRequestFailed: 'Impossible d’envoyer le code de vérification. Veuillez réessayer.',
    validEmail: 'Veuillez saisir une adresse e-mail valide.', showPassword: 'Afficher le mot de passe', hidePassword: 'Masquer le mot de passe'
  },
  es: {
    tagline: 'Portal ERP y de servicio',
    title: 'Iniciar sesión', subtitle: 'Acceda a su cuenta', resetSubtitle: 'Restablezca su contraseña.',
    methodCode: 'Con código', methodPassword: 'Con contraseña',
    emailLabel: 'Correo electrónico', passwordLabel: 'Contraseña', login: 'Iniciar sesión',
    identifierPlaceholder: 'nombre@ejemplo.ch',
    sendCode: 'Enviar código', codeSent: 'Hemos enviado un código de 6 dígitos a',
    codeLabel: 'Código de verificación', verify: 'Verificar e iniciar sesión', changeEmail: 'Cambiar correo electrónico',
    forgot: '¿Ha olvidado su contraseña?', resetTitle: 'Restablecer contraseña',
    resetHelp: 'Enviaremos las instrucciones a este correo electrónico.', sendReset: 'Enviar instrucciones',
    backToLogin: 'Volver al inicio de sesión', resetSent: 'Si existe una cuenta con este correo electrónico, se han enviado las instrucciones.', language: 'Idioma',
    errCreds: 'Introduzca su correo electrónico y contraseña.', errNoId: 'Introduzca su correo electrónico.',
    errCode: 'Introduzca el código de 6 dígitos.', networkError: 'No se puede conectar con el servidor. Compruebe su conexión a Internet.',
    invalidCredentials: 'El correo electrónico o la contraseña no son correctos.', unauthorized: 'Su cuenta no tiene autorización para acceder al sistema.',
    noAccount: 'No se encontró ninguna cuenta con este correo electrónico. Inicie sesión con contraseña o contacte con su administrador.',
    invalidCode: 'El código de verificación no es válido o ha caducado.', loginFailed: 'No se pudo iniciar sesión. Inténtelo de nuevo.',
    codeRequestFailed: 'No se pudo enviar el código de verificación. Inténtelo de nuevo.',
    validEmail: 'Introduzca un correo electrónico válido.', showPassword: 'Mostrar contraseña', hidePassword: 'Ocultar contraseña'
  }
};

const localizedAuthError = (error, t, fallbackKey) => {
  const message = String(error?.message || '');
  if (/network error|failed to fetch|unable to connect|server might be down/i.test(message)) return t('networkError');
  if (/incorrect email or password|invalid credentials/i.test(message)) return t('invalidCredentials');
  if (/not authorized|unauthorized/i.test(message)) return t('unauthorized');
  if (/no account found|account.*not found/i.test(message)) return t('noAccount');
  if (/invalid or expired.*code|code.*invalid or expired/i.test(message)) return t('invalidCode');
  return t(fallbackKey);
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { i18n } = useTranslation();
  const currentLangCode = i18n.language ? i18n.language.substring(0, 2).toLowerCase() : 'de';
  const lang = ['de', 'en', 'it', 'fr', 'es'].includes(currentLangCode) ? currentLangCode : 'de';
  const setLang = (newLang) => i18n.changeLanguage(newLang);
  const t = (k) => T[lang][k] || T.en[k] || k;

  const [authMethod, setAuthMethod] = useState('code'); // 'code' | 'password' — default code

  // Password login
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Code login
  const [step, setStep] = useState('idle');   // 'idle' | 'code'
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');

  // Password reset
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetRequested, setResetRequested] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Existing session? Verify and redirect.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (localStorage.getItem('passwordChangeRequired') === 'true') {
      navigate('/change-password', { state: { isFirstLogin: true } });
      return;
    }
    if (token) checkSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkSession = async () => {
    try {
      if (localStorage.getItem('passwordJustChanged') === 'true') {
        localStorage.removeItem('passwordJustChanged');
      }
      await userApi.getCurrentUser();
      if (localStorage.getItem('passwordChangeRequired') === 'true') {
        navigate('/change-password', { state: { isFirstLogin: true } });
        return;
      }
      redirectAfterLogin();
    } catch (err) {
      if (err.message.includes('password change')) {
        navigate('/change-password', { state: { isFirstLogin: true } });
      }
      // Otherwise stay on the login page.
    }
  };

  const redirectAfterLogin = () => {
    const savedRedirect = sessionStorage.getItem('redirectAfterLogin');
    const pendingOffer = location.state?.from?.pathname === '/communication'
      ? new URLSearchParams(location.state.from.search || '').get('voiceOffer') : null;
    const redirectTo = isVoiceReference(pendingOffer)
      ? '/communication?voiceOffer=' + pendingOffer
      : savedRedirect || location.state?.from?.pathname || '/';
    sessionStorage.removeItem('redirectAfterLogin');
    window.location.href = redirectTo;
  };

  // ── Password login ──
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) { setError(t('errCreds')); return; }
    setLoading(true);
    try {
      const data = await userApi.login(email, password);
      if (data.passwordChangeRequired) {
        navigate('/change-password', { state: { isFirstLogin: true } });
        return;
      }
      redirectAfterLogin();
    } catch (err) {
      setError(localizedAuthError(err, t, 'loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  // ── Code login (full staff session) ──
  const sendCode = async (e) => {
    if (e) e.preventDefault();
    const value = identifier.trim();
    if (!value) { setError(t('errNoId')); return; }
    setIdentifier(value);
    setError(''); setSuccess('');
    setLoading(true);
    try {
      await userApi.requestCustomerOtp(value); // /auth/customer/request-code — shared code email
      setStep('code');
    } catch (err) {
      setError(localizedAuthError(err, t, 'codeRequestFailed'));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    if (e) e.preventDefault();
    if (!code.trim() || code.trim().length !== 6) { setError(t('errCode')); return; }
    setError(''); setLoading(true);
    try {
      const data = await userApi.verifyLoginCode(identifier, code.trim());
      if (data.passwordChangeRequired) {
        navigate('/change-password', { state: { isFirstLogin: true } });
        return;
      }
      redirectAfterLogin();
    } catch (err) {
      setError(localizedAuthError(err, t, 'invalidCode'));
      setLoading(false);
    }
  };

  // ── Password reset ──
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!resetEmail || !resetEmail.includes('@')) { setError(t('validEmail')); return; }
    setLoading(true);
    try {
      await userApi.requestPasswordReset(resetEmail);
    } catch (_) { /* don't reveal whether the account exists */ }
    setResetRequested(true);
    setSuccess(true);
    setLoading(false);
  };

  const switchMethod = (m) => {
    if (!m) return;
    setAuthMethod(m);
    setError(''); setSuccess('');
    setStep('idle'); setCode('');
  };

  const greenBtn = {
    py: 1.4, borderRadius: '14px', bgcolor: '#16a34a', textTransform: 'none', fontWeight: 800,
    fontSize: '0.98rem', boxShadow: 'none', '&:hover': { bgcolor: '#15803d', boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#fff' }
  };
  const fieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: '14px', '&.Mui-focused fieldset': { borderColor: '#28a428' } },
    '& label.Mui-focused': { color: '#28a428' }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
      background: 'radial-gradient(1200px 600px at 50% -10%, #eafbe9 0%, #f6faf7 45%, #f1f5f9 100%)'
    }}>
      {/* Language selector with real flags */}
      <Box sx={{ position: 'absolute', top: 20, right: 20, width: 'min(420px, calc(100vw - 32px))' }}>
        <ToggleButtonGroup aria-label={t('language')} value={lang} exclusive size="small" onChange={(_, v) => v && setLang(v)}
          sx={{ width: '100%', flexWrap: 'wrap', justifyContent: 'flex-end', bgcolor: '#fff', '& .MuiToggleButton-root': { flex: '1 1 auto', minWidth: 48, px: 0.6, py: 0.5, fontSize: '0.75rem', fontWeight: 700, textTransform: 'none', whiteSpace: 'nowrap', border: '1px solid #e2e8f0', color: '#64748b', '&.Mui-selected': { bgcolor: '#eafbe9', color: '#1a7a34', borderColor: '#a7e8a7' } } }}>
          {LANGS.map(l => (
            <ToggleButton key={l.code} value={l.code} aria-label={l.label}>
              <Box component="img" src={flagSrc(l.flag)} alt="" sx={{ width: 18, height: 12, borderRadius: '2px', mr: 0.25 }} />
              {l.code.toUpperCase()}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ width: '100%', maxWidth: 420 }}>
        {/* Brand */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box component="img" src="/logo.png" alt="EscooterLab"
            sx={{ height: 72, width: 'auto', mb: 1, objectFit: 'contain' }} />
          <Typography sx={{ color: '#64748b', fontSize: '0.85rem' }}>{t('tagline')}</Typography>
        </Box>

        <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: '22px', border: '1px solid #eef2f7', boxShadow: '0 20px 50px rgba(15,23,42,0.08)' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: '#0f172a' }}>
            {showResetForm ? t('resetTitle') : t('title')}
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem', mb: 2.5 }}>
            {showResetForm ? t('resetSubtitle') : t('subtitle')}
          </Typography>

          {/* Method switch: Code / Password */}
          {!showResetForm && (
            <ToggleButtonGroup value={authMethod} exclusive fullWidth size="small"
              onChange={(_, v) => switchMethod(v)}
              sx={{ mb: 2.5, '& .MuiToggleButton-root': { py: 0.9, fontWeight: 800, textTransform: 'none', fontSize: '0.85rem', border: '1px solid #e2e8f0', color: '#64748b', '&.Mui-selected': { bgcolor: '#16a34a', color: '#fff', '&:hover': { bgcolor: '#15803d' } } } }}>
              <ToggleButton value="code">{t('methodCode')}</ToggleButton>
              <ToggleButton value="password">{t('methodPassword')}</ToggleButton>
            </ToggleButtonGroup>
          )}

          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{t('resetSent')}</Alert>}

          {showResetForm ? (
            /* ── Password reset ── */
            <Box component="form" onSubmit={handlePasswordReset}>
              {!resetRequested ? (
                <>
                  <TextField fullWidth autoFocus label={t('emailLabel')} value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)} helperText={t('resetHelp')} sx={fieldSx} />
                  <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ ...greenBtn, mt: 2 }}>
                    {loading ? <CircularProgress size={22} color="inherit" /> : t('sendReset')}
                  </Button>
                </>
              ) : (
                !success && <Typography sx={{ color: '#334155', fontSize: '0.9rem', textAlign: 'center', my: 2 }}>{t('resetSent')}</Typography>
              )}
              <Button fullWidth startIcon={<BackIcon />} onClick={() => { setShowResetForm(false); setResetRequested(false); setResetEmail(''); setError(''); setSuccess(''); }}
                sx={{ mt: 1, textTransform: 'none', color: '#64748b', fontWeight: 600 }}>
                {t('backToLogin')}
              </Button>
            </Box>
          ) : authMethod === 'password' ? (
            /* ── Password login ── */
            <Box component="form" onSubmit={handlePasswordLogin}>
              <TextField fullWidth autoFocus label={t('emailLabel')} value={email}
                onChange={(e) => setEmail(e.target.value)} autoComplete="email" sx={{ ...fieldSx, mb: 2 }} />
              <TextField fullWidth label={t('passwordLabel')} type={showPassword ? 'text' : 'password'}
                value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label={showPassword ? t('hidePassword') : t('showPassword')} onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={fieldSx} />
              <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ ...greenBtn, mt: 2 }}>
                {loading ? <CircularProgress size={22} color="inherit" /> : t('login')}
              </Button>
              <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                <Button variant="text" onClick={() => { setShowResetForm(true); setError(''); setSuccess(''); }}
                  sx={{ textTransform: 'none', color: '#64748b', fontWeight: 600 }}>
                  {t('forgot')}
                </Button>
              </Box>
            </Box>
          ) : (
            /* ── Code login ── */
            step === 'idle' ? (
              <Box component="form" onSubmit={sendCode}>
                <TextField fullWidth autoFocus label={t('emailLabel')} placeholder={t('identifierPlaceholder')}
                  value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton type="submit" disabled={loading} sx={{ bgcolor: '#16a34a', color: '#fff', '&:hover': { bgcolor: '#15803d' }, '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#fff' } }}>
                          {loading ? <CircularProgress size={20} color="inherit" /> : <ArrowIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px', pr: 0.5, '&.Mui-focused fieldset': { borderColor: '#28a428' } }, '& label.Mui-focused': { color: '#28a428' } }} />
                <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ ...greenBtn, mt: 2 }}>
                  {loading ? <CircularProgress size={22} color="inherit" /> : t('sendCode')}
                </Button>
              </Box>
            ) : (
              <Box component="form" onSubmit={verifyCode}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, color: '#16a34a' }}>
                  <CheckIcon sx={{ fontSize: 20 }} />
                  <Typography sx={{ fontSize: '0.85rem', color: '#334155' }}>
                    {t('codeSent')} <strong>{identifier}</strong>
                  </Typography>
                </Box>
                <TextField fullWidth autoFocus label={t('codeLabel')} placeholder="000000"
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputProps={{ maxLength: 6, style: { letterSpacing: '10px', textAlign: 'center', fontSize: '1.6rem', fontWeight: 800 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px', '&.Mui-focused fieldset': { borderColor: '#28a428' } } }} />
                <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ ...greenBtn, mt: 2 }}>
                  {loading ? <CircularProgress size={22} color="inherit" /> : t('verify')}
                </Button>
                <Button fullWidth startIcon={<BackIcon />} onClick={() => { setStep('idle'); setCode(''); setError(''); }}
                  sx={{ mt: 1, textTransform: 'none', color: '#64748b', fontWeight: 600 }}>
                  {t('changeEmail')}
                </Button>
              </Box>
            )
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default Login;
